import { ReactElement, useEffect, useState } from 'react';
import { Flex, Box } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import { Form, useFormik, FormikProvider, useField } from 'formik';
import { useToasts } from 'react-toast-notifications';
import styled from 'styled-components';

import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import { SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import Button from 'packages/kakapo-ui-kit/components/editors/Button';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import SettingsTable, { SettingsRow } from 'packages/kakapo-ui-kit/components/common/SettingsTable';
import Multilingual from 'packages/kakapo-ui-kit/components/forms/controls/Multilingual';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Field, { FieldType } from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import useLanguage from 'packages/kakapo-ui-application/hooks/useLanguage';
import { validator } from 'packages/kakapo-ui-services/factories/ValidatorFactory';
import FieldErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/FieldErrorMessage';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';

const Anchor = styled.a`
	color: ${(props) => props.theme.colors.primary};
`;

function CompetencyLevel(props: {
	index: number;
	level: graphql.CompetencyLevel;
	onDelete?: () => void;
}) {
	const name = `levels.${props.index}.title`;
	return (
		<SettingsRow onDelete={props.onDelete}>
			<Multilingual name={name} requiresFocus />
			<FieldErrorMessage name={name} />
		</SettingsRow>
	);
}

function CompetencyLevelSetConfigurator() {
	const { t } = useTranslation();

	const [{ value: count }] = useField<string>('count');
	const [, , { setValue: setLevels }] = useField<graphql.CreateCompetencyLevelInput[]>('levels');
	const language = useLanguage();

	useEffect(() => {
		const levels: graphql.CreateCompetencyLevelInput[] = [];

		for (let level = 0; level < Math.max(0, Math.min(10, parseInt(count))); level += 1) {
			levels.push({
				title: [
					{
						languageID: language.id,
						text: `${t('common.settings.competencyLevelSets.create.placeholder')} ${level + 1}`,
					},
				],
			});
		}

		setLevels(levels);
	}, [count]);

	return (
		<>
			<Box mt={3}>
				<Paragraph color="slateBlue">
					{t('common.settings.competencyLevelSets.create.explanation')}{' '}
					<Anchor>{t('common.learnMore')}</Anchor>
				</Paragraph>
			</Box>
			<Box mt={3}>
				<Paragraph color="slateBlue">
					{t('common.settings.competencyLevelSets.create.instruction')}
				</Paragraph>
			</Box>
			<Flex alignItems="center" mt={3}>
				<Paragraph color="slateBlue">
					{t('common.settings.competencyLevelSets.create.createSetWith')}
				</Paragraph>
				<Box mx={3} flexShrink={0} flexGrow={0}>
					<Field small unspaced name="count" type={FieldType.Number} hideError />
				</Box>
				<Paragraph color="slateBlue">
					{t('common.settings.competencyLevelSets.create.options')}
				</Paragraph>
			</Flex>
			<FieldErrorMessage name="count" />
		</>
	);
}

function CompetencyLevelSetEditor(props: {
	set?: graphql.CompetencyLevelSet;
	onSave?: (set: graphql.CompetencyLevelSet) => void;
}) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const language = useLanguage();
	const [confirmed, setConfirmed] = useState(!!props.set);

	const formik = useFormik({
		initialValues: {
			title: props.set?.title ?? [],
			levels:
				props.set?.levels ??
				Array.from(new Array(5)).map(
					(level): graphql.CompetencyLevel => ({
						id: '__draft',
						title: [
							{
								languageID: language.id,
								text: `${t('common.settings.competencyLevelSets.create.placeholder')} ${level + 1}`,
							},
						],
					}),
				),
			count: (props.set?.levels.length ?? 5).toFixed(),
		},
		onSubmit: () => onUpdate(),
		validationSchema: validator.object({
			title: validator
				.array(
					validator.object({
						text: validator.string().min(1).max(256).required(),
					}),
				)
				.min(1),

			levels: validator.array(
				validator.object({
					title: validator
						.array(
							validator.object({
								text: validator.string().min(1).max(256).required(),
							}),
						)
						.min(1),
				}),
			),
			count: validator.number().min(1).max(10),
		}),
	});

	const [error, attempt, reset] = useErrorBoundary();

	const onUpdate = attempt(async () => {
		reset();

		if (props.set) {
			const { data } = await update({
				variables: {
					input: {
						id: props.set.id,
						title: formik.values.title.map((string) => ({
							languageID: string.languageID,
							text: string.text,
						})),
						levels: formik.values.levels.map((level) => ({
							id: level.id,
							title: level.title.map((string) => ({
								languageID: string.languageID,
								text: string.text,
							})),
						})),
					},
				},
			});

			props.onSave?.(
				data?.updateCompetencyLevelSet.competencyLevelSet as graphql.CompetencyLevelSet,
			);
		} else {
			const { data } = await create({
				variables: {
					input: {
						title: formik.values.title.map((string) => ({
							languageID: string.languageID,
							text: string.text,
						})),
						levels: formik.values.levels.map((level) => ({
							title: level.title.map((string) => ({
								languageID: string.languageID,
								text: string.text,
							})),
						})),
					},
				},
			});

			if (props.onSave) {
				props.onSave(
					data?.createCompetencyLevelSet.competencyLevelSet as graphql.CompetencyLevelSet,
				);
			} else if (data) {
				history.push(
					`/settings/competency-level-sets/${data.createCompetencyLevelSet.competencyLevelSet.id}`,
				);
			}
		}

		addToast(t('common.settings.competencyLevelSets.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const [create, { loading: createLoading }] = graphql.useCreateCompetencyLevelSetMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateCompetencyLevelSetMutation();
	const loading = createLoading || updateLoading;

	const history = useHistory();

	return (
		<Flex flexDirection="column" mt={3}>
			<Box mb={2}>
				<Box mb={3}>
					<SubSectionTitle>{t('common.settings.competencyLevelSets.edit.details')}</SubSectionTitle>
				</Box>
				<HorizontalRule />
			</Box>
			<GraphQLErrorMessage error={error} showValidationDetails />
			<FormikProvider value={formik}>
				<Form>
					<DetailsTableForm
						formValues={[
							{
								label: t('common.settings.competencyLevelSets.edit.name'),
								name: 'title',
								type: DetailsTableFormValueTypes.Multilingual,
							},
						]}
					/>
				</Form>

				<Box mt={3}>
					<SettingsTable title={t('common.settings.competencyLevelSets.edit.levels')}>
						{props.set || confirmed ? (
							formik.values.levels.map((level, index) => (
								<CompetencyLevel
									key={level.id}
									index={index}
									level={level}
									onDelete={
										props.set
											? undefined
											: () => {
													const levels = formik.values.levels.slice();
													levels.splice(index, 1);
													formik.setFieldValue('levels', levels);
											  }
									}
								/>
							))
						) : (
							<CompetencyLevelSetConfigurator />
						)}
					</SettingsTable>
				</Box>
			</FormikProvider>

			<Flex mt={4}>
				{confirmed ? (
					<Button size={32} onClick={() => formik.submitForm()} isLoading={loading}>
						{t('common.save')}
					</Button>
				) : (
					<Button size={32} onClick={() => setConfirmed(true)}>
						{t('common.next')}
					</Button>
				)}
			</Flex>
		</Flex>
	);
}

function CompetencyLevelSetEditorID(props: { id: string }): ReactElement {
	const { data, error, loading } = graphql.useCompetencyLevelSetQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return <Spinner isLoading />;
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return (
			<CompetencyLevelSetEditor set={data!.competencyLevelSet as graphql.CompetencyLevelSet} />
		);
	}
}

export { CompetencyLevelSetEditor as CompetencyLevelSetCreator };

export default CompetencyLevelSetEditorID;
